import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Bug,
  Calendar,
  CalendarDays,
  Check,
  CheckCircle2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CircleDashed,
  CircleDot,
  Clock,
  FileText,
  Flag,
  Flame,
  Gauge,
  GripVertical,
  Hash,
  Inbox,
  Layers,
  ListTodo,
  Minus,
  MoveRight,
  MoveVertical,
  Plus,
  Play,
  Sparkles,
  Target,
  Trophy,
  Type as TypeIcon,
  User,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useIssueDrawerStore } from '@/stores/issue-drawer-store';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import { PageHeader } from '@/components/common/page-header';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { listMembers } from '@/lib/org-api';
import {
  createIssue,
  getProjectByKey,
  listStatuses,
  updateIssue,
  type IssuePriority,
  type IssueStatusRow,
  type IssueType,
} from '@/lib/project-api';
import {
  completeSprintApi,
  createSprint,
  getBacklogApi,
  moveIssueToSprint,
  startSprintApi,
  type BacklogIssueRow,
  type BacklogResponse,
  type SprintRow,
} from '@/lib/sprint-api';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

const BACKLOG_ID = '__BACKLOG__';

export function ProjectBacklogPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === orgId);
  const canManage = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('sprint.manage'),
  );
  const canCreateIssue = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('issue.create'),
  );

  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });

  const backlogQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'backlog'],
    queryFn: () =>
      projectQuery.data
        ? getBacklogApi(orgId, projectQuery.data.id)
        : Promise.resolve<BacklogResponse | null>(null),
    enabled: Boolean(projectQuery.data),
  });

  const statusesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'statuses'],
    queryFn: () =>
      projectQuery.data ? listStatuses(orgId, projectQuery.data.id) : Promise.resolve([]),
    enabled: Boolean(projectQuery.data),
  });

  const membersQuery = useQuery({
    queryKey: ['org', orgId, 'members', 'compact'],
    queryFn: () => listMembers(orgId, { limit: 100 }),
  });

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [createIssueTarget, setCreateIssueTarget] = useState<{
    sprintId: string | null;
    label: string;
  } | null>(null);
  const [completeSprint, setCompleteSprint] = useState<SprintRow | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [activeIssueKey, setActiveIssueKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const moveMutation = useMutation({
    mutationFn: ({ issueKey, sprintId }: { issueKey: string; sprintId: string | null }) =>
      moveIssueToSprint(orgId, issueKey, sprintId),
    onMutate: ({ issueKey, sprintId }) => {
      const key = ['org', orgId, 'project', projectKey, 'backlog'] as const;
      const previous = qc.getQueryData<BacklogResponse | null>(key);
      if (!previous) return { previous };
      const next = applyClientMove(previous, issueKey, sprintId);
      qc.setQueryData(key, next);
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['org', orgId, 'project', projectKey, 'backlog'], ctx.previous);
      }
      error('Move failed', extractApiErrorMessage(err));
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
    },
  });

  const updateIssueStatusMutation = useMutation({
    mutationFn: ({ issueKey, statusId }: { issueKey: string; statusId: string }) =>
      updateIssue(orgId, issueKey, { statusId }),
    onMutate: ({ issueKey, statusId }) => {
      const key = ['org', orgId, 'project', projectKey, 'backlog'] as const;
      const previous = qc.getQueryData<BacklogResponse | null>(key);
      if (!previous) return { previous };
      const nextStatus = statusesQuery.data?.find((s) => s.id === statusId);
      if (!nextStatus) return { previous };
      const patchRow = (row: BacklogIssueRow): BacklogIssueRow =>
        row.key === issueKey
          ? {
              ...row,
              statusId,
              statusName: nextStatus.name,
              statusCategory: nextStatus.category,
            }
          : row;
      const next: BacklogResponse = {
        ...previous,
        sprints: previous.sprints.map((sp) => ({
          ...sp,
          issues: sp.issues.map(patchRow),
        })),
        backlog: previous.backlog.map(patchRow),
      };
      qc.setQueryData(key, next);
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['org', orgId, 'project', projectKey, 'backlog'], ctx.previous);
      }
      error('Could not change status', extractApiErrorMessage(err));
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      if (vars) qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', vars.issueKey] });
    },
  });

  const updateIssueAssigneeMutation = useMutation({
    mutationFn: ({
      issueKey,
      assigneeId,
    }: {
      issueKey: string;
      assigneeId: string | null;
    }) => updateIssue(orgId, issueKey, { assigneeId }),
    onMutate: ({ issueKey, assigneeId }) => {
      const key = ['org', orgId, 'project', projectKey, 'backlog'] as const;
      const previous = qc.getQueryData<BacklogResponse | null>(key);
      if (!previous) return { previous };
      const nextMember = assigneeId
        ? membersQuery.data?.items.find((m) => m.userId === assigneeId)
        : null;
      const patchRow = (row: BacklogIssueRow): BacklogIssueRow =>
        row.key === issueKey
          ? {
              ...row,
              assigneeId: assigneeId,
              assigneeName: nextMember?.name ?? null,
            }
          : row;
      const next: BacklogResponse = {
        ...previous,
        sprints: previous.sprints.map((sp) => ({
          ...sp,
          issues: sp.issues.map(patchRow),
        })),
        backlog: previous.backlog.map(patchRow),
      };
      qc.setQueryData(key, next);
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['org', orgId, 'project', projectKey, 'backlog'], ctx.previous);
      }
      error('Could not change assignee', extractApiErrorMessage(err));
    },
    onSettled: (_data, _err, vars) => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      if (vars) qc.invalidateQueries({ queryKey: ['org', orgId, 'issue', vars.issueKey] });
    },
  });

  const startMutation = useMutation({
    mutationFn: (sprintId: string) => startSprintApi(orgId, projectQuery.data!.id, sprintId),
    onSuccess: () => {
      success('Sprint started');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
    },
    onError: (e) => error('Could not start sprint', extractApiErrorMessage(e)),
  });

  const completeMutation = useMutation({
    mutationFn: ({
      sprintId,
      targetSprintId,
    }: {
      sprintId: string;
      targetSprintId?: string | null;
    }) =>
      completeSprintApi(orgId, projectQuery.data!.id, sprintId, {
        carryoverTarget: targetSprintId ? 'SPRINT' : 'BACKLOG',
        ...(targetSprintId ? { targetSprintId } : {}),
      }),
    onSuccess: (res) => {
      success(
        'Sprint completed',
        `Carried over ${res.reassignedIssues} issue${res.reassignedIssues === 1 ? '' : 's'}`,
      );
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      setCompleteSprint(null);
    },
    onError: (e) => error('Could not complete sprint', extractApiErrorMessage(e)),
  });

  const handleDragStart = (evt: DragStartEvent) => {
    const key = String(evt.active.id).replace('issue:', '');
    setActiveIssueKey(key);
  };

  const handleDragEnd = (evt: DragEndEvent) => {
    setOverId(null);
    setActiveIssueKey(null);
    if (!evt.over || !evt.active) return;
    const sourceIssueKey = String(evt.active.id).replace('issue:', '');
    const target = String(evt.over.id).replace('container:', '');
    const targetSprintId = target === BACKLOG_ID ? null : target;

    const currentIssue = findIssueByKey(backlogQuery.data ?? null, sourceIssueKey);
    if (!currentIssue) return;
    const currentSprintId = currentIssue.sprintId ?? null;
    if (currentSprintId === targetSprintId) return;

    const activeSprintId = backlogQuery.data?.activeSprintId ?? null;
    // Active sprint is read-only for *moving issues out* but new issues may be added (scope creep).
    if (currentSprintId === activeSprintId && activeSprintId !== null) {
      error('Cannot move out of the active sprint', 'Complete the sprint first to move issues.');
      return;
    }

    moveMutation.mutate({ issueKey: sourceIssueKey, sprintId: targetSprintId });
  };

  if (projectQuery.isError) {
    return (
      <div className="mx-auto w-full max-w-6xl p-6">
        <EmptyState
          title="Could not load project"
          description={extractApiErrorMessage(projectQuery.error)}
        />
      </div>
    );
  }

  const data = backlogQuery.data;
  const activeSprintId = data?.activeSprintId ?? null;

  const totalBacklogSP = data?.backlog.reduce((sum, i) => sum + (i.storyPoints ?? 0), 0) ?? 0;
  const sprintsCount = data?.sprints.length ?? 0;
  const backlogCount = data?.backlog.length ?? 0;
  const plannedSP =
    data?.sprints.reduce((sum, s) => sum + s.plannedPoints, 0) ?? 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-3xl" aria-hidden />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Layers className="h-5 w-5" aria-hidden />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Backlog</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Plan sprints by dragging issues between the backlog and sprints.
              </p>
            </div>
          </div>
          {canManage ? (
            <Button onClick={() => setCreateOpen(true)} disabled={!projectQuery.data}>
              <Plus className="mr-1 h-4 w-4" aria-hidden /> Create sprint
            </Button>
          ) : null}
        </div>
        {data ? (
          <div className="grid grid-cols-2 gap-px border-t bg-border sm:grid-cols-4">
            <StatTile icon={Target} label="Sprints" value={String(sprintsCount)} />
            <StatTile
              icon={Play}
              label="Active"
              value={activeSprintId ? '1' : '—'}
              {...(activeSprintId ? { accent: 'emerald' as const } : {})}
            />
            <StatTile icon={Inbox} label="Backlog" value={`${backlogCount} issue${backlogCount === 1 ? '' : 's'}`} />
            <StatTile icon={Hash} label="Story points" value={`${plannedSP + totalBacklogSP} SP`} />
          </div>
        ) : null}
      </div>

      {backlogQuery.isLoading ? (
        <Skeleton className="h-72 w-full" />
      ) : backlogQuery.isError ? (
        <EmptyState
          title="Could not load backlog"
          description={extractApiErrorMessage(backlogQuery.error)}
          action={<Button onClick={() => backlogQuery.refetch()}>Retry</Button>}
        />
      ) : data ? (
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragOver={(evt) => setOverId(evt.over?.id ? String(evt.over.id) : null)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveIssueKey(null);
            setOverId(null);
          }}
        >
          <div className="space-y-4">
            {data.sprints.length === 0 ? (
              <EmptyState
                icon={ListTodo}
                title="No sprints yet"
                description={
                  canManage
                    ? 'Create a sprint to start planning work from the backlog.'
                    : 'No sprints have been created yet.'
                }
                action={
                  canManage ? (
                    <Button onClick={() => setCreateOpen(true)}>
                      <Plus className="mr-1 h-4 w-4" aria-hidden /> Create sprint
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              data.sprints.map((sprint) => (
                <SprintCard
                  key={sprint.id}
                  sprint={sprint}
                  orgId={orgId}
                  orgSlug={orgSlug}
                  projectKey={projectKey}
                  collapsed={!!collapsed[sprint.id]}
                  onToggleCollapse={() =>
                    setCollapsed((prev) => ({ ...prev, [sprint.id]: !prev[sprint.id] }))
                  }
                  overId={overId}
                  canManage={canManage}
                  canCreateIssue={canCreateIssue}
                  isActive={sprint.id === activeSprintId}
                  activeSprintId={activeSprintId}
                  activeIssueKey={activeIssueKey}
                  statuses={statusesQuery.data ?? []}
                  members={membersQuery.data?.items ?? []}
                  onChangeStatus={(issueKey, statusId) =>
                    updateIssueStatusMutation.mutate({ issueKey, statusId })
                  }
                  onChangeAssignee={(issueKey, assigneeId) =>
                    updateIssueAssigneeMutation.mutate({ issueKey, assigneeId })
                  }
                  onStart={(id) => startMutation.mutate(id)}
                  onComplete={(s) => setCompleteSprint(s)}
                  onAddIssue={(s) =>
                    setCreateIssueTarget({ sprintId: s.id, label: s.name })
                  }
                  starting={startMutation.isPending}
                />
              ))
            )}

            <Droppable id={`container:${BACKLOG_ID}`} over={overId === `container:${BACKLOG_ID}`}>
              <Card className="overflow-hidden">
                <div className="h-1 w-full bg-gradient-to-r from-slate-400/60 via-slate-500/40 to-transparent" />
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 bg-muted/30">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background text-muted-foreground ring-1 ring-border">
                      <Inbox className="h-4 w-4" aria-hidden />
                    </div>
                    <CardTitle className="text-base">Backlog</CardTitle>
                    <Badge variant="secondary" className="gap-1">
                      <ListTodo className="h-3 w-3" aria-hidden />
                      {data.backlog.length}
                    </Badge>
                    {totalBacklogSP > 0 ? (
                      <Badge variant="outline" className="gap-1 tabular-nums">
                        <Hash className="h-3 w-3" aria-hidden />
                        {totalBacklogSP} SP
                      </Badge>
                    ) : null}
                  </div>
                  {canCreateIssue ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setCreateIssueTarget({ sprintId: null, label: 'Backlog' })
                      }
                    >
                      <Plus className="mr-1 h-3 w-3" aria-hidden /> Add issue
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent className="pt-4">
                  {data.backlog.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background ring-1 ring-border">
                        <MoveVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
                      </div>
                      <p className="text-sm font-medium">Your backlog is empty</p>
                      <p className="text-xs text-muted-foreground">
                        Drop issues here to remove them from a sprint
                        {canCreateIssue ? ', or add a new one.' : '.'}
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {data.backlog.map((issue) => (
                        <IssueItem
                          key={issue.id}
                          issue={issue}
                          orgId={orgId}
                          orgSlug={orgSlug}
                          projectKey={projectKey}
                          statuses={statusesQuery.data ?? []}
                          members={membersQuery.data?.items ?? []}
                          onChangeStatus={(issueKey, statusId) =>
                            updateIssueStatusMutation.mutate({ issueKey, statusId })
                          }
                          onChangeAssignee={(issueKey, assigneeId) =>
                            updateIssueAssigneeMutation.mutate({ issueKey, assigneeId })
                          }
                          isOverlayActive={activeIssueKey === issue.key}
                        />
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </Droppable>
          </div>

          <DragOverlay dropAnimation={null}>
            {activeIssueKey
              ? (() => {
                  const active = findIssueByKey(data, activeIssueKey);
                  return active ? (
                    <IssueDragPreview issue={active} />
                  ) : null;
                })()
              : null}
          </DragOverlay>
        </DndContext>
      ) : (
        <EmptyState title="No project" description="Project not found." />
      )}

      <CreateSprintDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={orgId}
        projectId={projectQuery.data?.id ?? ''}
        projectKey={projectKey}
        onCreated={() =>
          qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] })
        }
      />

      <CompleteSprintDialog
        sprint={completeSprint}
        otherPlannedSprints={
          completeSprint
            ? (data?.sprints.filter((s) => s.id !== completeSprint.id && s.state === 'PLANNED') ??
              [])
            : []
        }
        orgId={orgId}
        projectId={projectQuery.data?.id ?? ''}
        onCancel={() => setCompleteSprint(null)}
        onConfirm={(targetSprintId) => {
          if (!completeSprint) return Promise.resolve();
          return completeMutation.mutateAsync({
            sprintId: completeSprint.id,
            ...(targetSprintId ? { targetSprintId } : {}),
          });
        }}
      />

      <CreateBacklogIssueDialog
        target={createIssueTarget}
        orgId={orgId}
        projectId={projectQuery.data?.id ?? ''}
        projectKey={projectKey}
        onClose={() => setCreateIssueTarget(null)}
        onCreated={() =>
          qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'backlog'] })
        }
      />
    </div>
  );
}

// -------------------- Sprint card --------------------

function SprintCard({
  sprint,
  orgId,
  orgSlug,
  projectKey,
  collapsed,
  onToggleCollapse,
  overId,
  canManage,
  canCreateIssue,
  isActive,
  activeSprintId,
  activeIssueKey,
  statuses,
  members,
  onChangeStatus,
  onChangeAssignee,
  onStart,
  onComplete,
  onAddIssue,
  starting,
}: {
  sprint: SprintRow & { issues: BacklogIssueRow[] };
  orgId: string;
  orgSlug: string;
  projectKey: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
  overId: string | null;
  canManage: boolean;
  canCreateIssue: boolean;
  isActive: boolean;
  activeSprintId: string | null;
  activeIssueKey: string | null;
  statuses: IssueStatusRow[];
  members: Array<{ userId: string; name: string }>;
  onChangeStatus: (issueKey: string, statusId: string) => void;
  onChangeAssignee: (issueKey: string, assigneeId: string | null) => void;
  onStart: (id: string) => void;
  onComplete: (s: SprintRow) => void;
  onAddIssue: (s: SprintRow) => void;
  starting: boolean;
}) {
  const overCapacity = sprint.plannedPoints > sprint.capacityPoints;
  const over = overId === `container:${sprint.id}`;
  const isScopeCreep = isActive && over;
  const canStart = sprint.state === 'PLANNED' && !activeSprintId;

  const capacityPct =
    sprint.capacityPoints > 0
      ? Math.min(100, Math.round((sprint.plannedPoints / sprint.capacityPoints) * 100))
      : 0;

  // Color-coded strip + header tint per sprint state.
  const stripClass = isActive
    ? 'bg-gradient-to-r from-emerald-500 via-emerald-400 to-transparent'
    : sprint.state === 'PLANNED'
      ? 'bg-gradient-to-r from-sky-500 via-sky-400 to-transparent'
      : 'bg-gradient-to-r from-slate-400 via-slate-300 to-transparent';

  const barColor = overCapacity
    ? 'bg-rose-500'
    : isActive
      ? 'bg-emerald-500'
      : 'bg-sky-500';

  return (
    <Droppable id={`container:${sprint.id}`} over={over}>
      <Card
        className={[
          'overflow-hidden transition-shadow',
          over ? 'shadow-md' : '',
          isActive ? 'ring-1 ring-emerald-500/30' : '',
        ].join(' ')}
      >
        <div className={['h-1 w-full', stripClass].join(' ')} />
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 bg-muted/30">
          <div className="flex min-w-0 items-center gap-2">
            <button
              type="button"
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
              aria-label={collapsed ? 'Expand sprint' : 'Collapse sprint'}
              onClick={onToggleCollapse}
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden />
              )}
            </button>
            <div
              className={[
                'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                isActive
                  ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400'
                  : sprint.state === 'PLANNED'
                    ? 'bg-sky-500/10 text-sky-600 ring-sky-500/30 dark:text-sky-400'
                    : 'bg-muted text-muted-foreground ring-border',
              ].join(' ')}
            >
              {isActive ? (
                <Play className="h-4 w-4" aria-hidden />
              ) : sprint.state === 'PLANNED' ? (
                <Target className="h-4 w-4" aria-hidden />
              ) : (
                <Trophy className="h-4 w-4" aria-hidden />
              )}
            </div>
            <CardTitle className="truncate text-base">{sprint.name}</CardTitle>
            {isActive ? (
              <Badge variant="success" className="gap-1">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" />
                Active
              </Badge>
            ) : null}
            {sprint.state === 'PLANNED' ? (
              <Badge variant="outline" className="border-sky-500/40 text-sky-700 dark:text-sky-300">
                Planned
              </Badge>
            ) : null}
            {sprint.state === 'COMPLETED' ? (
              <Badge variant="outline" className="text-muted-foreground">
                Completed
              </Badge>
            ) : null}
            <Badge variant="outline" className="gap-1 tabular-nums">
              <ListTodo className="h-3 w-3" aria-hidden />
              {sprint.issueCount}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {canCreateIssue && sprint.state !== 'COMPLETED' ? (
              <Button size="sm" variant="outline" onClick={() => onAddIssue(sprint)}>
                <Plus className="mr-1 h-3 w-3" aria-hidden /> Add issue
              </Button>
            ) : null}
            {canManage && sprint.state === 'PLANNED' ? (
              <Button size="sm" onClick={() => onStart(sprint.id)} disabled={!canStart || starting}>
                <Play className="mr-1 h-3 w-3" aria-hidden /> Start sprint
              </Button>
            ) : null}
            {canManage && isActive ? (
              <Button size="sm" variant="outline" onClick={() => onComplete(sprint)}>
                <Trophy className="mr-1 h-3 w-3" aria-hidden /> Complete sprint
              </Button>
            ) : null}
          </div>
        </CardHeader>
        {!collapsed ? (
          <CardContent className="space-y-3 pt-4">
            {sprint.goal ? (
              <div className="flex items-start gap-2 rounded-lg border bg-muted/20 p-3 text-sm">
                <Target className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                <p className="text-muted-foreground">{sprint.goal}</p>
              </div>
            ) : null}

            {/* Capacity bar */}
            <div>
              <div className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Hash className="h-3 w-3" aria-hidden />
                  Capacity
                </span>
                <span className="tabular-nums text-foreground">
                  <span className={overCapacity ? 'font-semibold text-rose-500' : 'font-semibold'}>
                    {sprint.plannedPoints}
                  </span>{' '}
                  / {sprint.capacityPoints} SP
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={['h-full rounded-full transition-all', barColor].join(' ')}
                  style={{ width: `${capacityPct}%` }}
                />
              </div>
            </div>

            {overCapacity && sprint.state === 'PLANNED' ? (
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Capacity exceeded — planned {sprint.plannedPoints} SP over the{' '}
                {sprint.capacityPoints} SP limit.
              </div>
            ) : null}
            {isScopeCreep ? (
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                Adding an issue to an active sprint creates scope creep.
              </div>
            ) : null}
            {sprint.issues.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-4 py-8 text-center">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background ring-1 ring-border">
                  <MoveVertical className="h-4 w-4 text-muted-foreground" aria-hidden />
                </div>
                <p className="text-xs text-muted-foreground">
                  No issues yet — drag from the backlog
                  {canCreateIssue ? ' or add one above.' : '.'}
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {sprint.issues.map((issue) => (
                  <IssueItem
                    key={issue.id}
                    issue={issue}
                    orgId={orgId}
                    orgSlug={orgSlug}
                    projectKey={projectKey}
                    statuses={statuses}
                    members={members}
                    onChangeStatus={onChangeStatus}
                    onChangeAssignee={onChangeAssignee}
                    locked={isActive}
                    isOverlayActive={activeIssueKey === issue.key}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        ) : null}
      </Card>
    </Droppable>
  );
}

// -------------------- Stat tile --------------------

function StatTile({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
  accent?: 'emerald';
}) {
  return (
    <div className="flex items-center gap-3 bg-background px-4 py-3">
      <div
        className={[
          'flex h-9 w-9 items-center justify-center rounded-lg ring-1',
          accent === 'emerald'
            ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400'
            : 'bg-primary/5 text-primary ring-primary/20',
        ].join(' ')}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

// -------------------- Issue list item (draggable) --------------------

const ISSUE_TYPE_META: Record<
  BacklogIssueRow['type'],
  { icon: typeof FileText; iconColor: string; bg: string }
> = {
  STORY: {
    icon: FileText,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10 ring-emerald-500/20',
  },
  TASK: {
    icon: CheckSquare,
    iconColor: 'text-sky-600 dark:text-sky-400',
    bg: 'bg-sky-500/10 ring-sky-500/20',
  },
  BUG: {
    icon: Bug,
    iconColor: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-500/10 ring-rose-500/20',
  },
  SUBTASK: {
    icon: CircleDot,
    iconColor: 'text-slate-500 dark:text-slate-400',
    bg: 'bg-muted ring-border',
  },
};

const ISSUE_PRIORITY_META: Record<
  BacklogIssueRow['priority'],
  { icon: typeof ArrowUp; color: string }
> = {
  LOWEST: { icon: ArrowDown, color: 'text-slate-400' },
  LOW: { icon: ArrowDown, color: 'text-emerald-500' },
  MEDIUM: { icon: Minus, color: 'text-amber-500' },
  HIGH: { icon: ArrowUp, color: 'text-orange-500' },
  HIGHEST: { icon: Flame, color: 'text-rose-500' },
};

const STATUS_CATEGORY_CLASS: Record<
  BacklogIssueRow['statusCategory'],
  string
> = {
  TODO: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  IN_PROGRESS: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  DONE: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300',
};

function IssueItem({
  issue,
  orgId,
  orgSlug,
  projectKey,
  statuses,
  members,
  onChangeStatus,
  onChangeAssignee,
  locked,
  isOverlayActive,
}: {
  issue: BacklogIssueRow;
  orgId: string;
  orgSlug: string;
  projectKey: string;
  statuses: IssueStatusRow[];
  members: Array<{ userId: string; name: string }>;
  onChangeStatus: (issueKey: string, statusId: string) => void;
  onChangeAssignee: (issueKey: string, assigneeId: string | null) => void;
  locked?: boolean;
  isOverlayActive?: boolean;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `issue:${issue.key}`,
    disabled: locked ?? false,
  });

  return (
    <li
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => openIssue({ orgId, orgSlug, projectKey, issueKey: issue.key })}
      className={[
        'group flex items-center gap-3 rounded-lg border bg-background px-2.5 py-2 text-sm shadow-sm transition-colors',
        // Active-sprint rows still react to hover + clicks (drawer open, inline
        // status edit). They just cannot be dragged out — which the dnd-kit
        // sensor already enforces. So we use the normal pointer cursor, not
        // the cursor-not-allowed "red no" icon.
        locked
          ? 'cursor-pointer hover:border-primary/40 hover:shadow-md'
          : 'cursor-grab hover:border-primary/40 hover:shadow-md active:cursor-grabbing',
        isOverlayActive ? 'border-dashed bg-muted/30 opacity-40' : '',
      ].join(' ')}
      aria-hidden={isOverlayActive ?? false}
    >
      <IssueRowInner
        issue={issue}
        orgId={orgId}
        orgSlug={orgSlug}
        projectKey={projectKey}
        statuses={statuses}
        members={members}
        onChangeStatus={onChangeStatus}
        onChangeAssignee={onChangeAssignee}
        locked={locked}
        interactive
      />
    </li>
  );
}

function IssueDragPreview({ issue }: { issue: BacklogIssueRow }) {
  return (
    <div
      className="flex items-center gap-3 rounded-lg border-2 border-primary/60 bg-background px-2.5 py-2 text-sm shadow-2xl ring-4 ring-primary/10"
      style={{ cursor: 'grabbing' }}
    >
      <IssueRowInner issue={issue} />
    </div>
  );
}

function IssueRowInner({
  issue,
  orgId,
  orgSlug,
  projectKey,
  statuses,
  members,
  onChangeStatus,
  onChangeAssignee,
  locked,
  interactive,
}: {
  issue: BacklogIssueRow;
  orgId?: string | undefined;
  orgSlug?: string | undefined;
  projectKey?: string | undefined;
  statuses?: IssueStatusRow[] | undefined;
  members?: Array<{ userId: string; name: string }> | undefined;
  onChangeStatus?: ((issueKey: string, statusId: string) => void) | undefined;
  onChangeAssignee?: ((issueKey: string, assigneeId: string | null) => void) | undefined;
  locked?: boolean | undefined;
  interactive?: boolean | undefined;
}) {
  const typeMeta = ISSUE_TYPE_META[issue.type];
  const TypeIconCmp = typeMeta.icon;
  const priMeta = ISSUE_PRIORITY_META[issue.priority];
  const PriIconCmp = priMeta.icon;
  const assigneeInitial = issue.assigneeName?.trim()?.charAt(0)?.toUpperCase() ?? '?';

  return (
    <>
      <GripVertical
        className={[
          'h-4 w-4 shrink-0 text-muted-foreground/50 transition-colors',
          locked ? 'opacity-30' : 'group-hover:text-muted-foreground',
        ].join(' ')}
        aria-hidden
      />

      <div
        className={[
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1',
          typeMeta.bg,
        ].join(' ')}
        title={issue.type}
      >
        <TypeIconCmp className={['h-3.5 w-3.5', typeMeta.iconColor].join(' ')} aria-hidden />
      </div>

      <PriIconCmp
        className={['h-4 w-4 shrink-0', priMeta.color].join(' ')}
        aria-label={`Priority ${issue.priority}`}
      />

      {interactive && orgId && orgSlug && projectKey ? (
        <IssueKeyButton
          issueKey={issue.key}
          orgId={orgId}
          orgSlug={orgSlug}
          projectKey={projectKey}
        />
      ) : (
        <span className="shrink-0 font-mono text-[11px] font-medium text-primary">{issue.key}</span>
      )}

      <span className="min-w-0 flex-1 truncate text-sm">{issue.title}</span>

      <div className="flex shrink-0 items-center gap-2 text-xs">
        {issue.storyPoints != null ? (
          <span
            className="inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-full bg-muted px-2 text-[11px] font-semibold tabular-nums ring-1 ring-border"
            title={`${issue.storyPoints} story points`}
          >
            {issue.storyPoints}
          </span>
        ) : null}
        {interactive && statuses && statuses.length > 0 && onChangeStatus ? (
          <InlineStatusSelect
            issue={issue}
            statuses={statuses}
            onChange={(statusId) => onChangeStatus(issue.key, statusId)}
          />
        ) : (
          <span
            className={[
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
              STATUS_CATEGORY_CLASS[issue.statusCategory],
            ].join(' ')}
          >
            {issue.statusName}
          </span>
        )}
        {interactive && members && onChangeAssignee ? (
          <InlineAssigneeSelect
            issue={issue}
            members={members}
            onChange={(assigneeId) => onChangeAssignee(issue.key, assigneeId)}
          />
        ) : issue.assigneeName ? (
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20"
            title={issue.assigneeName}
            aria-label={`Assigned to ${issue.assigneeName}`}
          >
            {assigneeInitial}
          </span>
        ) : (
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border"
            title="Unassigned"
            aria-label="Unassigned"
          >
            <User className="h-3 w-3" aria-hidden />
          </span>
        )}
      </div>
    </>
  );
}

function IssueKeyButton({
  issueKey,
  orgId,
  orgSlug,
  projectKey,
}: {
  issueKey: string;
  orgId: string;
  orgSlug: string;
  projectKey: string;
}) {
  const openIssue = useIssueDrawerStore((s) => s.open);
  return (
    <button
      type="button"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        openIssue({ orgId, orgSlug, projectKey, issueKey });
      }}
      className="shrink-0 font-mono text-[11px] font-medium text-primary hover:underline"
    >
      {issueKey}
    </button>
  );
}

function InlineAssigneeSelect({
  issue,
  members,
  onChange,
}: {
  issue: BacklogIssueRow;
  members: Array<{ userId: string; name: string }>;
  onChange: (assigneeId: string | null) => void;
}) {
  const initial = issue.assigneeName?.trim().charAt(0).toUpperCase() ?? '?';
  const assignedLabel = issue.assigneeName ?? 'Unassigned';

  return (
    <span
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex"
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            aria-label={`Assignee: ${assignedLabel}. Click to change.`}
            title={`Assignee: ${assignedLabel}`}
            className="flex h-6 w-6 items-center justify-center rounded-full ring-1 transition-all hover:ring-2 hover:ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-primary/60"
          >
            {issue.assigneeName ? (
              <span className="flex h-full w-full items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
                {initial}
              </span>
            ) : (
              <span className="flex h-full w-full items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border">
                <User className="h-3 w-3" aria-hidden />
              </span>
            )}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              'z-50 max-h-[min(22rem,60vh)] w-60 overflow-y-auto overflow-x-hidden rounded-xl border border-border/70 bg-popover p-1.5 text-popover-foreground shadow-xl ring-1 ring-black/5',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            )}
          >
            <AssigneeOption
              avatar={
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border">
                  <User className="h-3 w-3" aria-hidden />
                </span>
              }
              label={<span className="italic text-muted-foreground">Unassigned</span>}
              selected={issue.assigneeId == null}
              onSelect={() => onChange(null)}
            />
            <div className="my-1 h-px bg-border" role="separator" />
            {members.map((m) => {
              const mInitial = m.name.trim().charAt(0).toUpperCase() || '?';
              return (
                <AssigneeOption
                  key={m.userId}
                  avatar={
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold uppercase text-primary ring-1 ring-primary/20">
                      {mInitial}
                    </span>
                  }
                  label={m.name}
                  selected={issue.assigneeId === m.userId}
                  onSelect={() => onChange(m.userId)}
                />
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </span>
  );
}

function AssigneeOption({
  avatar,
  label,
  selected,
  onSelect,
}: {
  avatar: React.ReactNode;
  label: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className={cn(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-2 pr-6 text-sm outline-none transition-colors',
        'data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground',
        selected ? 'bg-primary/10 font-medium text-primary' : '',
      )}
    >
      {avatar}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {selected ? (
        <Check className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" aria-hidden />
      ) : null}
    </DropdownMenu.Item>
  );
}

function InlineStatusSelect({
  issue,
  statuses,
  onChange,
}: {
  issue: BacklogIssueRow;
  statuses: IssueStatusRow[];
  onChange: (statusId: string) => void;
}) {
  return (
    // Wrapper stops pointerdown + click propagating to the row (which would
    // otherwise open the drawer and fight the dnd-kit pointer sensor).
    <span
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex"
    >
      <Select
        variant="inline"
        value={issue.statusId}
        onChange={(e) => onChange(e.target.value)}
        wrapperClassName="w-auto"
        className="min-h-0 px-1 py-0.5"
      >
        {statuses.map((s) => (
          <option key={s.id} value={s.id}>
            <span
              className={[
                'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                STATUS_CATEGORY_CLASS[s.category],
              ].join(' ')}
            >
              {s.name}
            </span>
          </option>
        ))}
      </Select>
    </span>
  );
}

// -------------------- Droppable wrapper --------------------

function Droppable({
  id,
  over,
  children,
}: {
  id: string;
  over: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={['rounded-md transition-shadow', over ? 'ring-2 ring-primary' : ''].join(' ')}
    >
      {children}
    </div>
  );
}

// -------------------- Helpers --------------------

function findIssueByKey(data: BacklogResponse | null, issueKey: string): BacklogIssueRow | null {
  if (!data) return null;
  for (const sprint of data.sprints) {
    const hit = sprint.issues.find((i) => i.key === issueKey);
    if (hit) return hit;
  }
  return data.backlog.find((i) => i.key === issueKey) ?? null;
}

function applyClientMove(
  data: BacklogResponse,
  issueKey: string,
  targetSprintId: string | null,
): BacklogResponse {
  let moving: BacklogIssueRow | null = null;

  const stripped = {
    ...data,
    sprints: data.sprints.map((sprint) => ({
      ...sprint,
      issues: sprint.issues.filter((i) => {
        if (i.key === issueKey) {
          moving = { ...i, sprintId: targetSprintId };
          return false;
        }
        return true;
      }),
    })),
    backlog: data.backlog.filter((i) => {
      if (i.key === issueKey) {
        if (!moving) moving = { ...i, sprintId: targetSprintId };
        return false;
      }
      return true;
    }),
  };

  if (!moving) return stripped;

  if (targetSprintId === null) {
    stripped.backlog = [moving, ...stripped.backlog];
  } else {
    stripped.sprints = stripped.sprints.map((s) =>
      s.id === targetSprintId ? { ...s, issues: [...s.issues, moving!] } : s,
    );
  }
  // Recompute plannedPoints on the touched sprints.
  stripped.sprints = stripped.sprints.map((s) => ({
    ...s,
    plannedPoints: s.issues.reduce((sum, i) => sum + (i.storyPoints ?? 0), 0),
    issueCount: s.issues.length,
  }));
  return stripped;
}

// -------------------- Create dialog --------------------

type DurationPreset = '1w' | '2w' | '3w' | '4w' | 'custom';

const DURATION_PRESETS: Array<{ id: DurationPreset; label: string; weeks: number }> = [
  { id: '1w', label: '1 week', weeks: 1 },
  { id: '2w', label: '2 weeks', weeks: 2 },
  { id: '3w', label: '3 weeks', weeks: 3 },
  { id: '4w', label: '4 weeks', weeks: 4 },
];

function toISODate(d: Date): string {
  // YYYY-MM-DD for <input type="date">
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatHumanDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function CreateSprintDialog({
  open,
  onOpenChange,
  orgId,
  projectId,
  projectKey,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  projectId: string;
  projectKey: string;
  onCreated: () => void;
}) {
  void projectKey;
  const { success, error } = useToast();
  const [name, setName] = useState('');
  const [goal, setGoal] = useState('');
  const [capacity, setCapacity] = useState('40');
  const [duration, setDuration] = useState<DurationPreset>('2w');
  const [startDate, setStartDate] = useState<string>(toISODate(new Date()));
  const [endDate, setEndDate] = useState<string>(() => {
    const end = new Date();
    end.setDate(end.getDate() + 14);
    return toISODate(end);
  });
  const [submitting, setSubmitting] = useState(false);

  // Auto-sync end date whenever preset or start date changes.
  const applyPreset = (preset: DurationPreset, startIso: string) => {
    setDuration(preset);
    if (preset === 'custom') return;
    const weeks = DURATION_PRESETS.find((p) => p.id === preset)?.weeks ?? 2;
    const s = new Date(startIso);
    if (Number.isNaN(s.getTime())) return;
    s.setDate(s.getDate() + weeks * 7);
    setEndDate(toISODate(s));
  };

  const onStartChange = (iso: string) => {
    setStartDate(iso);
    if (duration !== 'custom') applyPreset(duration, iso);
  };

  const reset = () => {
    setName('');
    setGoal('');
    setCapacity('40');
    setDuration('2w');
    const today = new Date();
    setStartDate(toISODate(today));
    const end = new Date();
    end.setDate(end.getDate() + 14);
    setEndDate(toISODate(end));
  };

  const bumpCapacity = (delta: number) => {
    const current = capacity.trim() === '' ? 0 : Number(capacity);
    const safe = Number.isFinite(current) ? current : 40;
    const next = Math.min(500, Math.max(1, safe + delta));
    setCapacity(String(next));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await createSprint(orgId, projectId, {
        name: name.trim(),
        ...(goal.trim() ? { goal: goal.trim() } : {}),
        ...(capacity ? { capacityPoints: Number(capacity) || 40 } : {}),
        ...(startDate ? { startDate: new Date(startDate).toISOString() } : {}),
        ...(endDate ? { endDate: new Date(endDate).toISOString() } : {}),
      });
      success('Sprint created');
      reset();
      onCreated();
      onOpenChange(false);
    } catch (err) {
      error('Create failed', extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const durationDays = (() => {
    if (!startDate || !endDate) return null;
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
    const ms = e.getTime() - s.getTime();
    if (ms < 0) return null;
    return Math.round(ms / (1000 * 60 * 60 * 24));
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-sky-500/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/20 dark:text-sky-400">
              <Target className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Create sprint</DialogTitle>
                <DialogDescription className="mt-1 text-xs">
                  Sprints start in the <span className="font-medium text-foreground">Planned</span>{' '}
                  state. Drag issues in from the backlog once created.
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form className="space-y-5 px-6 pb-6 pt-5" onSubmit={submit} noValidate>
          {/* Name */}
          <div className="space-y-2">
            <Label
              htmlFor="sp-name"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Flag className="h-3.5 w-3.5" aria-hidden /> Name
            </Label>
            <Input
              id="sp-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sprint 4"
              className="h-11 text-sm"
            />
          </div>

          {/* Goal */}
          <div className="space-y-2">
            <Label
              htmlFor="sp-goal"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Sparkles className="h-3.5 w-3.5" aria-hidden /> Goal{' '}
              <span className="ml-1 font-normal normal-case text-muted-foreground/80">(optional)</span>
            </Label>
            <Input
              id="sp-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="What does success look like?"
              className="h-11 text-sm"
            />
          </div>

          {/* Duration presets */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Clock className="h-3.5 w-3.5" aria-hidden /> Duration
            </Label>
            <div className="grid grid-cols-5 gap-2">
              {DURATION_PRESETS.map((preset) => {
                const selected = duration === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset.id, startDate)}
                    aria-pressed={selected}
                    className={[
                      'flex flex-col items-center justify-center rounded-lg border bg-background px-2 py-2 text-[11px] font-medium transition-all',
                      selected
                        ? 'border-sky-500/60 bg-sky-500/10 text-sky-700 ring-2 ring-sky-500/40 dark:text-sky-300'
                        : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                    ].join(' ')}
                  >
                    <span className="text-sm font-semibold tabular-nums">{preset.weeks}w</span>
                    <span className="text-[10px] uppercase tracking-wide">{preset.label.split(' ')[1]}</span>
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setDuration('custom')}
                aria-pressed={duration === 'custom'}
                className={[
                  'flex flex-col items-center justify-center rounded-lg border bg-background px-2 py-2 text-[11px] font-medium transition-all',
                  duration === 'custom'
                    ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/40'
                    : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                ].join(' ')}
              >
                <CalendarDays className="h-4 w-4" aria-hidden />
                <span className="text-[10px] uppercase tracking-wide">Custom</span>
              </button>
            </div>
          </div>

          {/* Dates */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="sp-start"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Calendar className="h-3.5 w-3.5" aria-hidden /> Starts
              </Label>
              <Input
                id="sp-start"
                type="date"
                value={startDate}
                onChange={(e) => onStartChange(e.target.value)}
                className="h-11 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="sp-end"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Calendar className="h-3.5 w-3.5" aria-hidden /> Ends
              </Label>
              <Input
                id="sp-end"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setDuration('custom');
                }}
                className="h-11 text-sm"
              />
            </div>
          </div>

          {/* Range summary */}
          {startDate && endDate ? (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs">
              <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 font-medium ring-1 ring-border">
                <Calendar className="h-3 w-3" aria-hidden />
                {formatHumanDate(startDate)}
              </span>
              <MoveRight className="h-3 w-3 text-muted-foreground" aria-hidden />
              <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-0.5 font-medium ring-1 ring-border">
                <Calendar className="h-3 w-3" aria-hidden />
                {formatHumanDate(endDate)}
              </span>
              {durationDays != null ? (
                <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-primary/5 px-2 py-0.5 font-medium text-primary ring-1 ring-primary/20">
                  <Clock className="h-3 w-3" aria-hidden />
                  {durationDays} day{durationDays === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="ml-auto inline-flex items-center gap-1 text-rose-500">
                  <AlertTriangle className="h-3 w-3" aria-hidden /> End is before start
                </span>
              )}
            </div>
          ) : null}

          {/* Capacity stepper */}
          <div className="space-y-2">
            <Label
              htmlFor="sp-cap"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <Gauge className="h-3.5 w-3.5" aria-hidden /> Capacity (story points)
            </Label>
            <div className="flex h-11 items-stretch overflow-hidden rounded-lg border">
              <button
                type="button"
                onClick={() => bumpCapacity(-1)}
                className="flex w-11 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Decrease capacity"
              >
                <Minus className="h-4 w-4" aria-hidden />
              </button>
              <input
                id="sp-cap"
                type="number"
                min={1}
                max={500}
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                className="w-full min-w-0 border-x border-input bg-background text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
              />
              <button
                type="button"
                onClick={() => bumpCapacity(1)}
                className="flex w-11 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Increase capacity"
              >
                <Plus className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()} className="gap-1.5">
              {submitting ? (
                <>
                  <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Creating…
                </>
              ) : (
                <>
                  <Target className="h-4 w-4" aria-hidden /> Create sprint
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Complete sprint dialog --------------------

function CompleteSprintDialog({
  sprint,
  otherPlannedSprints,
  orgId,
  projectId,
  onCancel,
  onConfirm,
}: {
  sprint: SprintRow | null;
  otherPlannedSprints: SprintRow[];
  orgId: string;
  projectId: string;
  onCancel: () => void;
  onConfirm: (targetSprintId: string | null) => Promise<unknown>;
}) {
  const { error } = useToast();
  // target is '' (backlog), 'new-sprint', or an existing sprint id
  const [target, setTarget] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  // Inline "new sprint" form fields
  const [newName, setNewName] = useState('');
  const [newDuration, setNewDuration] = useState<DurationPreset>('2w');

  if (!sprint) {
    return (
      <ConfirmDialog
        open={false}
        title=""
        onCancel={onCancel}
        onConfirm={() => Promise.resolve()}
      />
    );
  }

  const carryoverPoints = Math.max(0, sprint.plannedPoints - sprint.completedPoints);
  const completedPct =
    sprint.plannedPoints > 0
      ? Math.min(100, Math.round((sprint.completedPoints / sprint.plannedPoints) * 100))
      : 0;
  const allDone = carryoverPoints === 0 && sprint.plannedPoints > 0;

  const submit = async () => {
    if (submitting) return;
    if (target === 'new-sprint' && !newName.trim()) {
      error('Name required', 'Give the new sprint a name.');
      return;
    }
    setSubmitting(true);
    try {
      let targetId: string | null;
      if (target === 'new-sprint') {
        const weeks = DURATION_PRESETS.find((p) => p.id === newDuration)?.weeks ?? 2;
        const start = new Date();
        const end = new Date();
        end.setDate(end.getDate() + weeks * 7);
        const created = await createSprint(orgId, projectId, {
          name: newName.trim(),
          startDate: start.toISOString(),
          endDate: end.toISOString(),
        });
        targetId = created.id;
      } else {
        targetId = target || null;
      }
      await onConfirm(targetId);
    } catch (err) {
      error('Could not complete sprint', extractApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const chosenSprint = otherPlannedSprints.find((s) => s.id === target);

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-amber-500/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10 text-amber-600 ring-1 ring-amber-500/20 dark:text-amber-400">
              <Trophy className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="truncate text-xl">
                  Complete <span className="text-primary">{sprint.name}</span>?
                </DialogTitle>
                <DialogDescription className="mt-1 text-xs">
                  {allDone ? (
                    <>All issues are done. You can still pick a carryover target for safety.</>
                  ) : (
                    <>
                      Incomplete issues will be moved to the backlog or rolled over to another
                      planned sprint.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-6 pb-6 pt-5">
          {/* Progress overview */}
          <div className="space-y-3 rounded-xl border bg-muted/20 p-4">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Gauge className="h-3.5 w-3.5" aria-hidden /> Progress
              </span>
              <span className="tabular-nums text-foreground">{completedPct}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                style={{ width: `${completedPct}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 pt-1">
              <CompletionStat
                icon={Target}
                label="Planned"
                value={`${sprint.plannedPoints} SP`}
                tone="primary"
              />
              <CompletionStat
                icon={CheckCircle2}
                label="Completed"
                value={`${sprint.completedPoints} SP`}
                tone="emerald"
              />
              <CompletionStat
                icon={Inbox}
                label="Carryover"
                value={`${carryoverPoints} SP`}
                tone={carryoverPoints > 0 ? 'amber' : 'muted'}
              />
            </div>
          </div>

          {/* Carryover target picker */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <MoveRight className="h-3.5 w-3.5" aria-hidden /> Move incomplete issues to
            </Label>

            <div className="space-y-2">
              {/* Backlog option */}
              <button
                type="button"
                onClick={() => setTarget('')}
                aria-pressed={target === ''}
                className={[
                  'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all',
                  target === ''
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                    : 'border-input hover:bg-muted/60',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                    target === ''
                      ? 'bg-primary/10 text-primary ring-primary/30'
                      : 'bg-muted text-muted-foreground ring-border',
                  ].join(' ')}
                >
                  <Inbox className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Backlog</div>
                  <div className="text-xs text-muted-foreground">
                    Incomplete issues return to the project backlog.
                  </div>
                </div>
                <div
                  className={[
                    'flex h-5 w-5 items-center justify-center rounded-full ring-1',
                    target === ''
                      ? 'bg-primary text-primary-foreground ring-primary'
                      : 'ring-border',
                  ].join(' ')}
                  aria-hidden
                >
                  {target === '' ? <CheckCircle2 className="h-4 w-4" /> : null}
                </div>
              </button>

              {/* Sprint options */}
              {otherPlannedSprints.map((s) => {
                const selected = target === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setTarget(s.id)}
                    aria-pressed={selected}
                    className={[
                      'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all',
                      selected
                        ? 'border-sky-500/60 bg-sky-500/5 ring-2 ring-sky-500/30'
                        : 'border-input hover:bg-muted/60',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                        selected
                          ? 'bg-sky-500/10 text-sky-600 ring-sky-500/30 dark:text-sky-400'
                          : 'bg-muted text-muted-foreground ring-border',
                      ].join(' ')}
                    >
                      <Target className="h-4 w-4" aria-hidden />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {s.plannedPoints} / {s.capacityPoints} SP planned
                      </div>
                    </div>
                    <div
                      className={[
                        'flex h-5 w-5 items-center justify-center rounded-full ring-1',
                        selected ? 'bg-sky-500 text-white ring-sky-500' : 'ring-border',
                      ].join(' ')}
                      aria-hidden
                    >
                      {selected ? <CheckCircle2 className="h-4 w-4" /> : null}
                    </div>
                  </button>
                );
              })}

              {/* Create new sprint option */}
              <button
                type="button"
                onClick={() => setTarget('new-sprint')}
                aria-pressed={target === 'new-sprint'}
                className={[
                  'flex w-full items-center gap-3 rounded-lg border border-dashed px-3 py-2.5 text-left transition-all',
                  target === 'new-sprint'
                    ? 'border-emerald-500/60 bg-emerald-500/5 ring-2 ring-emerald-500/30'
                    : 'border-input hover:bg-muted/60',
                ].join(' ')}
              >
                <div
                  className={[
                    'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                    target === 'new-sprint'
                      ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/30 dark:text-emerald-400'
                      : 'bg-muted text-muted-foreground ring-border',
                  ].join(' ')}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">Create new sprint</div>
                  <div className="text-xs text-muted-foreground">
                    Spin up a fresh sprint and carry incomplete issues into it.
                  </div>
                </div>
                <div
                  className={[
                    'flex h-5 w-5 items-center justify-center rounded-full ring-1',
                    target === 'new-sprint'
                      ? 'bg-emerald-500 text-white ring-emerald-500'
                      : 'ring-border',
                  ].join(' ')}
                  aria-hidden
                >
                  {target === 'new-sprint' ? <CheckCircle2 className="h-4 w-4" /> : null}
                </div>
              </button>

              {/* Inline new-sprint form */}
              {target === 'new-sprint' ? (
                <div className="space-y-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="new-sprint-name"
                      className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      <Flag className="h-3 w-3" aria-hidden /> Sprint name
                    </Label>
                    <Input
                      id="new-sprint-name"
                      required
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder={`${sprint.name.replace(/\d+$/, (n) => String(Number(n) + 1)) || 'Next sprint'}`}
                      className="h-10 text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Clock className="h-3 w-3" aria-hidden /> Duration
                    </Label>
                    <div className="grid grid-cols-4 gap-2">
                      {DURATION_PRESETS.map((p) => {
                        const selected = newDuration === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setNewDuration(p.id)}
                            aria-pressed={selected}
                            className={[
                              'flex flex-col items-center justify-center rounded-md border bg-background px-2 py-1.5 text-[10px] font-medium transition-all',
                              selected
                                ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 ring-2 ring-emerald-500/40 dark:text-emerald-300'
                                : 'border-input text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                            ].join(' ')}
                          >
                            <span className="text-sm font-semibold tabular-nums">{p.weeks}w</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Carryover warning for chosen sprint over-capacity */}
          {chosenSprint &&
          chosenSprint.plannedPoints + carryoverPoints > chosenSprint.capacityPoints ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Rolling over will push <strong>{chosenSprint.name}</strong> above its{' '}
              {chosenSprint.capacityPoints} SP capacity.
            </div>
          ) : null}
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-4">
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || (target === 'new-sprint' && !newName.trim())}
            className="gap-1.5"
          >
            {submitting ? (
              <>
                <CircleDashed className="h-4 w-4 animate-spin" aria-hidden />{' '}
                {target === 'new-sprint' ? 'Creating & completing…' : 'Completing…'}
              </>
            ) : (
              <>
                <Trophy className="h-4 w-4" aria-hidden /> Complete sprint
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompletionStat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Target;
  label: string;
  value: string;
  tone: 'primary' | 'emerald' | 'amber' | 'muted';
}) {
  const toneCls =
    tone === 'primary'
      ? 'bg-primary/5 text-primary ring-primary/20'
      : tone === 'emerald'
        ? 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400'
        : tone === 'amber'
          ? 'bg-amber-500/10 text-amber-600 ring-amber-500/20 dark:text-amber-400'
          : 'bg-muted text-muted-foreground ring-border';
  return (
    <div className="flex items-center gap-2 rounded-lg bg-background p-2 ring-1 ring-border">
      <div className={['flex h-8 w-8 items-center justify-center rounded-md ring-1', toneCls].join(' ')}>
        <Icon className="h-4 w-4" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-sm font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

// -------------------- Create-issue-in-backlog-or-sprint dialog --------------------

const TYPE_META: Record<
  'STORY' | 'TASK' | 'BUG',
  {
    label: string;
    icon: typeof FileText;
    tint: string;
    ring: string;
    iconColor: string;
  }
> = {
  STORY: {
    label: 'Story',
    icon: FileText,
    tint: 'bg-emerald-500/10',
    ring: 'ring-emerald-500/60 border-emerald-500/60',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
  },
  TASK: {
    label: 'Task',
    icon: CheckSquare,
    tint: 'bg-sky-500/10',
    ring: 'ring-sky-500/60 border-sky-500/60',
    iconColor: 'text-sky-600 dark:text-sky-400',
  },
  BUG: {
    label: 'Bug',
    icon: Bug,
    tint: 'bg-rose-500/10',
    ring: 'ring-rose-500/60 border-rose-500/60',
    iconColor: 'text-rose-600 dark:text-rose-400',
  },
};

const PRIORITY_META: Record<
  IssuePriority,
  {
    label: string;
    icon: typeof ArrowUp;
    iconColor: string;
    ringColor: string;
  }
> = {
  LOWEST: {
    label: 'Lowest',
    icon: ArrowDown,
    iconColor: 'text-slate-500 dark:text-slate-400',
    ringColor: 'ring-slate-500/50 border-slate-500/50',
  },
  LOW: {
    label: 'Low',
    icon: ArrowDown,
    iconColor: 'text-emerald-500',
    ringColor: 'ring-emerald-500/50 border-emerald-500/50',
  },
  MEDIUM: {
    label: 'Medium',
    icon: Minus,
    iconColor: 'text-amber-500',
    ringColor: 'ring-amber-500/50 border-amber-500/50',
  },
  HIGH: {
    label: 'High',
    icon: ArrowUp,
    iconColor: 'text-orange-500',
    ringColor: 'ring-orange-500/50 border-orange-500/50',
  },
  HIGHEST: {
    label: 'Highest',
    icon: Flame,
    iconColor: 'text-rose-500',
    ringColor: 'ring-rose-500/50 border-rose-500/50',
  },
};

const CREATE_TYPE_OPTIONS: Array<'STORY' | 'TASK' | 'BUG'> = ['STORY', 'TASK', 'BUG'];
const CREATE_PRIORITY_OPTIONS: IssuePriority[] = ['LOWEST', 'LOW', 'MEDIUM', 'HIGH', 'HIGHEST'];

function CreateBacklogIssueDialog({
  target,
  orgId,
  projectId,
  projectKey,
  onClose,
  onCreated,
}: {
  target: { sprintId: string | null; label: string } | null;
  orgId: string;
  projectId: string;
  projectKey: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<'STORY' | 'TASK' | 'BUG'>('TASK');
  const [priority, setPriority] = useState<IssuePriority>('MEDIUM');
  const [storyPoints, setStoryPoints] = useState('');
  const [description, setDescription] = useState('');

  const open = target !== null;
  const isSprintTarget = (target?.sprintId ?? null) !== null;

  const reset = () => {
    setTitle('');
    setType('TASK');
    setPriority('MEDIUM');
    setStoryPoints('');
    setDescription('');
  };

  const mutation = useMutation({
    mutationFn: () => {
      if (!target) return Promise.reject(new Error('No target'));
      const desc = description.trim();
      const sp = storyPoints.trim();
      return createIssue(orgId, projectId, {
        type: type as IssueType,
        title: title.trim(),
        priority,
        sprintId: target.sprintId,
        ...(desc ? { descriptionMarkdown: desc } : {}),
        ...(sp ? { storyPoints: Number(sp) } : {}),
      });
    },
    onSuccess: (issue) => {
      success('Issue created', issue.key);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'board'] });
      onCreated();
      reset();
      onClose();
    },
    onError: (e) => error('Could not create issue', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim() || mutation.isPending) return;
    mutation.mutate();
  };

  const bumpPoints = (delta: number) => {
    const current = storyPoints.trim() === '' ? 0 : Number(storyPoints);
    const safe = Number.isFinite(current) ? current : 0;
    const next = Math.min(999, Math.max(0, safe + delta));
    setStoryPoints(String(next));
  };

  const TargetIcon = isSprintTarget ? Target : Inbox;
  const SelectedTypeIcon = TYPE_META[type].icon;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Sparkles className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Create issue</DialogTitle>
                <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-[11px] ring-1 ring-border">
                    {projectKey}-•••
                  </span>
                  <span>will be created in</span>
                  <span
                    className={[
                      'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1',
                      isSprintTarget
                        ? 'bg-primary/10 text-primary ring-primary/30'
                        : 'bg-muted text-foreground ring-border',
                    ].join(' ')}
                  >
                    <TargetIcon className="h-3 w-3" aria-hidden />
                    {target?.label ?? 'Backlog'}
                  </span>
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form className="space-y-5 px-6 pb-6 pt-5" onSubmit={submit} noValidate>
          {/* Type chips */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <TypeIcon className="h-3.5 w-3.5" aria-hidden /> Type
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {CREATE_TYPE_OPTIONS.map((t) => {
                const meta = TYPE_META[t];
                const Icon = meta.icon;
                const selected = t === type;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setType(t)}
                    aria-pressed={selected}
                    className={[
                      'group flex items-center justify-center gap-2 rounded-lg border bg-background px-3 py-2.5 text-sm font-medium transition-all',
                      selected
                        ? `${meta.tint} ${meta.ring} ring-2 shadow-sm`
                        : 'border-input hover:bg-muted/60',
                    ].join(' ')}
                  >
                    <Icon
                      className={['h-4 w-4', selected ? meta.iconColor : 'text-muted-foreground'].join(
                        ' ',
                      )}
                      aria-hidden
                    />
                    <span>{meta.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label
              htmlFor="bl-new-title"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <CircleDot className="h-3.5 w-3.5" aria-hidden /> Summary
            </Label>
            <div className="relative">
              <SelectedTypeIcon
                className={[
                  'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
                  TYPE_META[type].iconColor,
                ].join(' ')}
                aria-hidden
              />
              <Input
                id="bl-new-title"
                required
                maxLength={200}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                placeholder="What needs to be done?"
                className="h-11 pl-9 text-sm"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums text-muted-foreground">
                {title.length}/200
              </span>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {/* Priority */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Flame className="h-3.5 w-3.5" aria-hidden /> Priority
              </Label>
              <div className="flex items-center gap-1 rounded-lg border bg-muted/30 p-1">
                {CREATE_PRIORITY_OPTIONS.map((p) => {
                  const meta = PRIORITY_META[p];
                  const Icon = meta.icon;
                  const selected = p === priority;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPriority(p)}
                      aria-pressed={selected}
                      title={meta.label}
                      aria-label={meta.label}
                      className={[
                        'flex h-8 flex-1 items-center justify-center rounded-md transition-all',
                        selected
                          ? `bg-background shadow-sm ring-1 ${meta.ringColor}`
                          : 'hover:bg-background/60',
                      ].join(' ')}
                    >
                      <Icon
                        className={['h-4 w-4', selected ? meta.iconColor : 'text-muted-foreground'].join(
                          ' ',
                        )}
                        aria-hidden
                      />
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{PRIORITY_META[priority].label}</span>
              </p>
            </div>

            {/* Story points stepper */}
            <div className="space-y-2">
              <Label
                htmlFor="bl-new-sp"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Hash className="h-3.5 w-3.5" aria-hidden /> Story points
              </Label>
              <div className="flex h-10 items-stretch overflow-hidden rounded-lg border">
                <button
                  type="button"
                  onClick={() => bumpPoints(-1)}
                  className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Decrease story points"
                >
                  <Minus className="h-4 w-4" aria-hidden />
                </button>
                <input
                  id="bl-new-sp"
                  type="number"
                  min={0}
                  max={999}
                  value={storyPoints}
                  onChange={(e) => setStoryPoints(e.target.value)}
                  placeholder="—"
                  className="w-full min-w-0 border-x border-input bg-background text-center text-sm font-medium tabular-nums focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring"
                />
                <button
                  type="button"
                  onClick={() => bumpPoints(1)}
                  className="flex w-10 items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label="Increase story points"
                >
                  <Plus className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label
              htmlFor="bl-new-desc"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden /> Description
            </Label>
            <div className="relative rounded-lg border focus-within:ring-2 focus-within:ring-ring">
              <textarea
                id="bl-new-desc"
                className="min-h-[110px] w-full resize-y rounded-lg bg-background p-3 pr-16 text-sm focus:outline-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={10_000}
                placeholder="Add more context… Markdown supported."
              />
              <span className="pointer-events-none absolute bottom-2 right-3 text-[10px] tabular-nums text-muted-foreground">
                {description.length}/10000
              </span>
            </div>
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                reset();
                onClose();
              }}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !projectId || !title.trim()}
              className="gap-1.5"
            >
              {mutation.isPending ? (
                <>
                  <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Creating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" aria-hidden /> Create issue
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
